import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Phone,
  Globe,
  Clock3,
  MapPin,
  Languages,
  Building2,
  Info,
} from "lucide-react";

export default function DoctorCard({ doctor }) {
  const isAdminVerified =
    doctor?.adminVerified === true ||
    doctor?.admin_verified === true ||
    doctor?.admin_verified === 1 ||
    doctor?.admin_verified === "1" ||
    doctor?.verification_status === "verified" ||
    doctor?.admin_review_status === "verified";

    const verifiedDate =
    doctor?.admin_verified_at ||
    doctor?.admin_reviewed_at ||
    doctor?.lastVerified ||
    doctor?.last_verified ||
    doctor?.last_updated ||
    "Recently reviewed";

  const sourceLabel = isAdminVerified
    ? "Admin reviewed / verified"
    : doctor?.source &&
      doctor.source !== "SAMPLE/DEMO DATA" &&
      doctor.source !== "sample/demo data"
    ? doctor.source
    : "Public information / pending admin review";

    const websiteUrl =
    doctor?.website
      ? doctor.website.startsWith("http://") ||
        doctor.website.startsWith("https://")
        ? doctor.website
        : `https://${doctor.website}`
      : null;


  return (
    <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#DCECF7] flex items-center justify-center text-[#244B6B] font-bold text-lg shrink-0">
            {doctor?.name?.charAt(0) || "D"}
          </div>

          <div>
            <h3 className="text-[17px] font-bold text-slate-900 leading-snug">
              {doctor.name}
            </h3>

            <p className="text-sm text-slate-500 mt-1">{doctor.specialty}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 items-end">
          {doctor.accepting ? (
            <span className="px-3 py-1 rounded-full text-[11px] font-semibold border border-[#BFD7FF] bg-[#EEF4FF] text-[#356DFF]">
              Likely Accepting
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-[11px] font-semibold border border-[#F4D58D] bg-[#FFF8E8] text-[#B7791F]">
              Call to Confirm
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <span className="px-3 py-1 rounded-full bg-[#EEF4FF] text-[#356DFF] text-[11px] font-semibold">
          family
        </span>

        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">
          {doctor.city}
        </span>

        <span className="px-3 py-1 rounded-full bg-[#EAFBF3] text-[#0E9F6E] text-[11px] font-semibold">
          Recently Updated
        </span>

        {isAdminVerified && (
          <span className="px-3 py-1 rounded-full bg-[#EEF4FF] text-[#356DFF] text-[11px] font-semibold">
            Admin Verified
          </span>
        )}
      </div>

      <div className="mt-5 space-y-3 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <Building2 size={15} />
          <span>{doctor.clinic}</span>
        </div>

        <div className="flex items-center gap-2">
          <MapPin size={15} />
          <span>{doctor.address}</span>
        </div>

        <div className="flex items-center gap-2">
          <Languages size={15} />
          <span>{doctor.languages}</span>
        </div>
      </div>

      <div className="mt-5 border border-[#D7E5FF] rounded-2xl p-4 bg-[#F8FBFF]">
        <div className="flex items-center gap-2 text-[#356DFF] text-sm font-medium">
          <ShieldCheck size={16} />
          <span>
            {isAdminVerified ? "Admin verified:" : "Last verified:"}{" "}
            {verifiedDate}
          </span>
        </div>

        <div className="text-xs text-slate-400 mt-2">
          Source: {sourceLabel}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-[11px] tracking-[3px] font-bold text-slate-500 uppercase">
          <div className="flex items-center gap-1">
            Confidence
            <Info size={12} />
          </div>

          <span>{doctor.confidence || doctor.confidence_score || 40}/100</span>
        </div>

        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500"
            style={{
              width: `${doctor.confidence || doctor.confidence_score || 40}%`,
            }}
          />
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-3 flex items-start gap-2 text-sm text-slate-500">
        <Clock3 size={16} className="mt-0.5 shrink-0" />

        <span>
          Availability information is continuously reviewed and updated.
        </span>
      </div>

      <div className="mt-5 flex gap-3">
        <a
          href={`tel:${doctor.phone}`}
          className="flex-1 bg-[#356DFF] hover:bg-[#2457E6] transition text-white rounded-2xl py-3 font-semibold flex items-center justify-center gap-2 text-sm"
        >
          <Phone size={16} />
          Call Clinic
        </a>

        <a
          href={websiteUrl || "#"}
          target="_blank"
          rel="noreferrer"
          className="flex-1 border border-slate-200 hover:border-[#356DFF] transition rounded-2xl py-3 font-semibold flex items-center justify-center gap-2 text-sm text-slate-700"
         >
          <Globe size={16} />
          Website
        </a>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <button className="px-4 py-2 rounded-full bg-[#EAFBF3] text-[#18794E] text-sm font-medium">
          Report update
        </button>

        <Link
          to={`/doctor/${doctor.id}`}
          className="text-sm font-semibold text-slate-800 hover:text-[#356DFF] transition"
        >
          View details →
        </Link>
      </div>
    </div>
  );
}